import './bootstrap';
import '../css/app.css';
import {createApp, h, ref, watch} from 'vue';
import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import ToastService from "primevue/toastservice";
import {definePreset} from "@primevue/themes";
import {ZiggyVue} from '../../vendor/tightenco/ziggy';

import 'vue3-perfect-scrollbar/style.css';
import DefaultLayout from "./client/administration/layouts/DefaultLayout.vue";
import EmptyLayout from "./client/administration/layouts/EmptyLayout.vue";
import {createInertiaApp, router} from "@inertiajs/vue3";
import Toast from "primevue/toast";
import ConfirmationService from "primevue/confirmationservice";
import KeyFilter from "primevue/keyfilter";
import Tooltip from "primevue/tooltip";
import {createPinia} from "pinia";
import _ from "lodash";
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
import 'overlayscrollbars/overlayscrollbars.css';
import {BProgress} from "@bprogress/core";
import consentConfig from "./consentConfig.js";
import { I18n } from 'laravel-vue-i18n';
import { register } from 'swiper/element/bundle';
import * as Sentry from "@sentry/vue";
import {PerfectScrollbarPlugin} from "vue3-perfect-scrollbar";


const appName = import.meta.env.VITE_APP_NAME || 'Roori';

BProgress.configure({
    parent: '#progress-bar',
    easing: 'ease-in-out',
})

register();

const resolve = (name) => {
    const pages = import.meta.glob('./client/**/*.vue', {eager: true})
    let page = pages[`./client/${name}.vue`]

    if (!page) {
        console.error(`Component ${name} not found`);
    }

    page.default.layout = name.startsWith('administration') ? DefaultLayout : EmptyLayout

    return page;
}

// Appear @ Route Start
router.on('start', () => {
    try{
        _.debounce(() => {
            document.querySelector('body').classList.add('loading');
            BProgress.start();
        }, 500)
    } catch(e){
        console.log(e)
    }
})

// Appear @ Route Finish
router.on('finish', () => {
    BProgress.done();
    document.querySelector('body').classList.remove('loading');
})
const pinia = createPinia()

createInertiaApp({
    title: (title) => title ? `${title} - ${appName}` : appName,
    resolve: (name) => resolve(name),
    setup({el, App, props, plugin}) {
        const lang = props.initialPage.props.locale || import.meta.env.VITE_APP_LOCALE || 'en';
        // Překlady teď chodí jako Inertia::once() prop (viz HandleInertiaRequests /
        // WidgetController) místo samostatného fetch() na /api/internationalization/strings —
        // Inertia se sama postará o to, že se stejný payload znovu neposílá při SPA navigaci.
        const translations = props.initialPage.props.translations || {};
        const I18nOptions = {
            shared: true,
            lang: lang,
            fallbackLang: lang,
            resolve: async () => ({ default: translations }),
        }

        const i18n = I18n.getSharedInstance(I18nOptions, true)

        const I18nVue = {
            install(app, options = {}) {
                options = {...options}
                app.config.globalProperties.$t = (key, replacements) => i18n.trans(key, replacements);
                app.config.globalProperties.$tChoice = (key, number, replacements) => i18n.transChoice(key, number, replacements);
                app.provide('i18n', i18n);
            }
        };


        const app = createApp({render: () => h(App, props)});
        app.use(plugin)
        app.use(ZiggyVue)
        app.use(pinia)
        app.use(PerfectScrollbarPlugin)
        app.component('OverlayScrollbars', OverlayScrollbarsComponent);
        app.directive('keyfilter', KeyFilter);
        app.directive('tooltip', Tooltip);
        app.use(I18nVue)
        app.use(PrimeVue, {
            theme: {
                options: {
                    prefix: '',
                    darkModeSelector: '.dark',
                    cssLayer: false
                },
                preset: definePreset(Aura, {
                    semantic: {
                        primary: {
                            50: '{blue.50}',
                            100: '{blue.100}',
                            200: '{blue.200}',
                            300: '{blue.300}',
                            400: '{blue.400}',
                            500: '#003CFF',
                            600: '{blue.600}',
                            700: '{blue.700}',
                            800: '{blue.800}',
                            900: '{blue.900}',
                            950: '{blue.950}'
                        }
                    }
                })
            }
        })
        app.use(ConfirmationService)
        app.use(ToastService)
        app.use(consentConfig)
        app.component('Toast', Toast)
        try {
            Sentry.init({
                app,
                dsn: "https://4c73397eb4c514cce2e23e89bdffcc9e@o4509414068322304.ingest.de.sentry.io/4509414111379536",
                // Setting this option to true will send default PII data to Sentry.
                // For example, automatic IP address collection on events
                sendDefaultPii: true,
                integrations: [
                    Sentry.browserTracingIntegration()
                ],
                // Tracing
                tracesSampleRate: 0.1, // Capture 100% of the transactions
                // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                tracePropagationTargets: ["localhost", "https://eu-1.roori.app"]
            });
            Sentry.setUser({
                id: props.initialPage.props?.auth?.user?.id,
                email: props.initialPage.props?.auth?.user?.email
            });
        } catch (e) {
            console.warn('Sentry failed to init', e);
        }

        app.mount(el)
    },
});

const runColorMode = (fn) => {
    if (!window.matchMedia) {
        return;
    }

    const query = window.matchMedia('(prefers-color-scheme: dark)');

    fn(query.matches);

    query.addEventListener('change', (event) => fn(event.matches));
}

runColorMode((isDarkMode) => {
    if (isDarkMode) {
        document.querySelector('html')?.classList.add('dark');
    } else {
        document.querySelector('html')?.classList.remove('dark');
    }
})
